/** Validation for the API key credentials a customer enters to install Oracle Cloud.
 *
 * P0 supports the commercial realm (`oc1`) only, so every OCID pattern pins it.
 */

const TENANCY_OCID = /^ocid1\.tenancy\.oc1\.[a-z0-9-]*\.[a-z0-9]+$/;
const USER_OCID = /^ocid1\.user\.oc1\.[a-z0-9-]*\.[a-z0-9]+$/;
const DOMAIN_OCID = /^ocid1\.domain\.oc1\.[a-z0-9-]*\.[a-z0-9]+$/;
const FINGERPRINT = /^([0-9a-f]{2}:){15}[0-9a-f]{2}$/;
const REGION_ID = /^[a-z]{2}-[a-z]+-\d+$/;

/** A PEM private key, with an optional `OCI_API_KEY` marker after the footer.
 *
 * The Oracle Cloud console appends that marker to the private keys it
 * generates. Encrypted keys don't match, because P0 signs requests without a
 * passphrase.
 */
const PEM_PRIVATE_KEY =
  /^-----BEGIN ((?:RSA )?PRIVATE KEY)-----([\s\S]*?)-----END \1-----(?:\s*OCI_API_KEY)?$/;
const BASE64 = /^[A-Za-z0-9+/]+={0,2}$/;

/** Rebuilds a pasted PEM private key, or returns undefined if it isn't one.
 *
 * The install form's secret input is a single line, so the browser drops the
 * key's line breaks on paste. This re-wraps the body at 64 characters.
 */
export const normalizePrivateKey = (input: string): string | undefined => {
  const match = input.trim().match(PEM_PRIVATE_KEY);
  if (!match) return undefined;
  const [, label, body] = match;
  const base64 = (body ?? "").replace(/\s+/g, "");
  if (!BASE64.test(base64)) return undefined;
  const lines = base64.match(/.{1,64}/g) ?? [];
  return [`-----BEGIN ${label}-----`, ...lines, `-----END ${label}-----`].join(
    "\n"
  );
};

const required =
  (name: string, check: (value: string) => string | undefined) =>
  async (_id: string, field: string | undefined) => {
    const value = field?.trim();
    if (!value) return `Please provide the ${name}`;
    return check(value);
  };

export const validateDomainId = required("domain OCID", (value) =>
  DOMAIN_OCID.test(value)
    ? undefined
    : "Domain OCID must look like ocid1.domain.oc1..<unique ID>"
);

export const validateTenancyId = required("tenancy OCID", (value) =>
  TENANCY_OCID.test(value)
    ? undefined
    : "Tenancy OCID must look like ocid1.tenancy.oc1..<unique ID>"
);

export const validateUserId = required("user OCID", (value) =>
  USER_OCID.test(value)
    ? undefined
    : "User OCID must look like ocid1.user.oc1..<unique ID>"
);

export const validateFingerprint = required("API key fingerprint", (value) =>
  // The install stores the fingerprint lowercased
  FINGERPRINT.test(value.toLowerCase())
    ? undefined
    : "Fingerprint must look like 12:34:56:78:9a:bc:de:f0:12:34:56:78:9a:bc:de:f0"
);

export const validateRegion = required("home region", (value) =>
  REGION_ID.test(value)
    ? undefined
    : "Region must be a region identifier, such as us-ashburn-1"
);

export const validatePrivateKey = async (
  _id: string,
  field: { clearText?: string; hash?: string } | undefined
) => {
  // A reconfigure that keeps the stored key sends only its hash
  if (field?.hash && !field.clearText) return undefined;
  if (!field?.clearText) return "Please provide the API private key";
  if (!normalizePrivateKey(field.clearText)) {
    return "Paste the whole private key file, from -----BEGIN PRIVATE KEY----- to -----END PRIVATE KEY-----. Keys protected by a passphrase aren't supported.";
  }
};
